import React, { useCallback, useEffect, useState } from 'react'
import { SkeletonList } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/recordsService'
import UploadDocumentDialog from './components/UploadDocumentDialog'
import { parseUtcDate } from '../../utils/dates'

function fmtSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const CATEGORY_LABEL = {
  LAB_REPORT: 'Lab report',
  IMAGING: 'Imaging',
  REFERRAL: 'Referral',
  INSURANCE: 'Insurance',
  OTHER: 'Other',
}

export default function DocumentsPage() {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploadOpen, setUploadOpen] = useState(false)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const res = await svc.listDocuments(currentProfile)
      setData(res)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setData(null)
    refresh()
  }, [refresh])

  async function deleteOne(id) {
    if (!window.confirm('Delete this document?')) return
    await svc.deleteDocument(currentProfile, id)
    await refresh()
  }

  async function toggleShare(doc) {
    const practitionerId = window.prompt(
      'Share with practitioner — enter their id (free-text in this demo):',
      doc.sharedWithPractitionerIds?.[0] || '',
    )
    if (!practitionerId) return
    const alreadyShared = (doc.sharedWithPractitionerIds || []).includes(practitionerId)
    await svc.shareDocument(currentProfile, doc.id, {
      practitionerId,
      revoke: alreadyShared,
    })
    await refresh()
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/records">Back to Records</BackLink>
        <Row $between $wrap>
          <div>
            <Pill>Records</Pill>
            <PageHeading style={{ marginTop: 12 }}>Documents</PageHeading>
            <PageSubheading>
              Upload lab reports, imaging, and referral letters. Share with practitioners ahead of visits.
            </PageSubheading>
          </div>
          <GhostButton onClick={() => setUploadOpen(true)}>+ Upload</GhostButton>
        </Row>

        {loading && !data ? (
          <SkeletonList count={3} />
        ) : !data?.items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No documents yet.</p>
            <Muted>Upload anything you&apos;d like your practitioner to see — pre-visit lab reports are common.</Muted>
          </div>
        ) : (
          <Stack $gap="10px">
            {data.items.map((d) => (
              <Card key={d.id}>
                <Row $between $gap="12px" $wrap>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Row $gap="8px">
                      <span style={{ fontSize: 22 }}>📎</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {d.fileName}
                        </div>
                        <Muted>
                          {CATEGORY_LABEL[d.category] || d.category} · {fmtSize(d.sizeBytes)} ·{' '}
                          {parseUtcDate(d.uploadedAt).toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Muted>
                        {d.description && <Muted style={{ marginTop: 4 }}>{d.description}</Muted>}
                        {d.sharedWithPractitionerIds?.length > 0 && (
                          <Pill $tone="success" style={{ marginTop: 8 }}>
                            Shared with {d.sharedWithPractitionerIds.length}
                          </Pill>
                        )}
                      </div>
                    </Row>
                  </div>
                  <Row $gap="8px">
                    <a
                      href={d.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        textDecoration: 'none',
                        background: 'transparent',
                        border: '1px solid #E6E0D6',
                        padding: '8px 14px',
                        borderRadius: 999,
                        color: '#1F3A5F',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      Open
                    </a>
                    <button
                      onClick={() => toggleShare(d)}
                      style={{
                        background: 'transparent',
                        border: '1px solid #E6E0D6',
                        padding: '8px 14px',
                        borderRadius: 999,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#1F3A5F',
                      }}
                    >
                      Share
                    </button>
                    <button
                      onClick={() => deleteOne(d.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        color: '#C24D49',
                      }}
                    >
                      Delete
                    </button>
                  </Row>
                </Row>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      <UploadDocumentDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={async (body) => {
          await svc.uploadDocument(currentProfile, body)
          await refresh()
        }}
      />
    </ContentMaxWidth>
  )
}
